import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        loadProfile(session.user);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        loadProfile(session.user);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (authUser) => {
    try {
      let { data, error } = await supabase
        .from('users')
        .select('*, company:company_profile(*)')
        .eq('auth_user_id', authUser.id)
        .single();
      if (error || !data) {
        const res = await supabase
          .from('users')
          .select('*, company:company_profile(*)')
          .eq('email', authUser.email)
          .single();
        data = res.data;
      }
      setUser(data || { email: authUser.email, full_name: authUser.email.split('@')[0] });
    } catch (err) {
      setUser({ email: authUser.email, full_name: authUser.email.split('@')[0] });
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { full_name: fullName } }
    });
    if (error) throw error;
    try {
      await supabase.from('app_events').insert({
        event_type: 'signup', app_name: 'geertsfarms',
        user_email: email, metadata: { full_name: fullName, source: 'web' },
      });
    } catch (e) { /* silent */ }
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
